import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import SubHeader from "components/common/SubHeader";
import ThumbnailCarousel from "components/shopping/ThumbnailCarousel";
import TabHeader from "components/common/TabHeader";
import NoData from "components/common/NoData";
import TuiViewer from "components/common/TuiViewer";
import ErrorPage from "pages/error/ErrorPage";
import { getTravelProduct, getTravelDepartures } from "api/travel";
import useUserStore from "@store/useUserStore";
import LoginBottomSheet from "components/common/LoginBottomSheet";
import { trackViewItem } from "utils/analytics";
import { getCurrencyConfig } from "utils/currency";
import { useSnackbar } from "contexts/SnackbarContext";
import useShare from "hooks/useShare";
import ProfileAvatar from "components/common/ProfileAvatar";
import PersonChip from "components/profile/PersonChip";
import { maskName } from "utils/util";
import { getPublicUserKey } from "utils/userIdentity";
import { renderWithLinks } from "utils/renderWithLinks";
import shareIcon from "assets/images/icons/ic_share.svg";
import "assets/scss/pages/AdminRegistTour.scss";
import Seo from "components/common/Seo";
import dayjs from "dayjs";
import { startDirectMessageRoom } from "api/chat";
import { TourHeartIcon } from "components/tour/TourLikeButton";
import useTourLikes from "hooks/useTourLikes";

const menus = ["상세정보", "문의"];

const fmt = (n) => {
    const num = Number(n);
    if (isNaN(num)) return "0";
    return num.toLocaleString("ko-KR");
};

const fmtScheduleDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const days = ["일", "월", "화", "수", "목", "금", "토"];
    return `${d.getMonth() + 1}/${d.getDate()} (${days[d.getDay()]})`;
};

const getRefundColorClass = (color) => {
    if (!color) return "green";
    const c = color.toLowerCase();
    if (c === "green" || c === "#4caf50") return "green";
    if (c === "orange" || c === "#ff9800" || c === "#ff5722") return "orange";
    if (c === "red" || c === "#f44336" || c === "#c62828") return "red";
    return "green";
};

const PRICE_OPTION_LABELS = {
    flightExcluded: "항공 별도",
    localCostIncluded: "현지 비용 포함",
    cardInstallment: "카드 할부 가능",
};

const TourDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { tourId } = useParams();

    // 관리자 미리보기는 state.tour 로 실제 payload 를 넘긴다. state 자체의 truthiness 로 판별하면
    // 라우팅 메타(푸시 cold-start 표식 등)만 실린 진입도 미리보기로 오판해 API 를 건너뛰고 빈 화면이 된다.
    const fromAdminData = location.state?.tour ? location.state : null;
    const userInfo = useUserStore((state) => state.userInfo);
    const { share } = useShare();
    const { isLiked, toggleLike } = useTourLikes();

    const [tour, setTour] = useState(null);
    const [errorStatus, setErrorStatus] = useState(null);
    const [selectedTab, setSelectedTab] = useState("상세정보");
    const [showLoginBottomSheet, setShowLoginBottomSheet] = useState(false);
    const [purchaseCnt, setPurchaseCnt] = useState(1);
    const [carouselImages, setCarouselImages] = useState([]);
    const [departures, setDepartures] = useState([]);
    const [scheduleValue, setScheduleValue] = useState(""); // "" | departure id string | "custom"
    const [desiredStartDate, setDesiredStartDate] = useState("");
    const [desiredEndDate, setDesiredEndDate] = useState("");
    const ctaRef = useRef(null);
    const [ctaHeight, setCtaHeight] = useState(200);

    const { showError } = useSnackbar();

    useEffect(() => {
        if (!ctaRef.current) return;
        const ro = new ResizeObserver(() => {
            setCtaHeight(ctaRef.current.getBoundingClientRect().height + 24);
        });
        ro.observe(ctaRef.current);
        return () => ro.disconnect();
    }, []);

    const [participantsExpanded, setParticipantsExpanded] = useState(true);


    useEffect(() => {
        if (fromAdminData) {
            setTour(fromAdminData.tour);
        } else {
            getTravelProduct(tourId).then((res) => {
              const images = res.images?.length ? res.images : (res.legacy_images || []).map(i => i.image)
              setTour(res);
              setCarouselImages(images)
              trackViewItem({
                  item_id: res.id,
                  item_name: res.name,
                  item_category: "tour",
                  price: res.price,
              });
              if (res.tour_type === "ALWAYS") {
                  getTravelDepartures(tourId, "RECRUITING").then((deps) => {
                      const today = dayjs();
                      const list = Array.isArray(deps) ? deps : deps?.results || [];
                      const validDepartures = list.filter(l => dayjs(l.start_date).isAfter(today))

                      setDepartures(validDepartures);
                      if (validDepartures.length === 0) setScheduleValue("custom");
                  });
              }
            }).catch((error) => {
              setErrorStatus(error.response?.status === 404 ? 404 : 500);
            });
        }
    }, [tourId, fromAdminData]);

    if (errorStatus) {
        return (
            <ErrorPage
                status={errorStatus}
                onRetry={errorStatus === 500 ? () => { setErrorStatus(null); setTour(null); } : undefined}
            />
        );
    }

    if (!tour) return null;

    // detailed_content 는 빈 값이 `" "`, `""`, `<p><br></p>` 등으로 올 수 있어
    // 텍스트/이미지 실질 콘텐츠가 있을 때만 상세 페이지 영역을 노출한다.
    const detailedText = (tour.detailed_content || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, "").trim();
    const hasDetailContent = /<img/i.test(tour.detailed_content || "") || detailedText.length > 0;

    const isLimited = tour.tour_type !== "ALWAYS";
    const guides = tour.guides || [];
    const guide = guides[0];
    const activeOptions = Object.entries(tour.price_options || {})
        .filter(([, v]) => v)
        .map(([k]) => PRICE_OPTION_LABELS[k])
        .filter(Boolean);


        const handleStartMessage = async (userId) => {
          if (!userInfo) {
              navigate("/login");
              return;
          }
          try {
              const room = await startDirectMessageRoom(userId);
              if (room?.id) {
                  navigate(`/messages/${encodeURIComponent(room.id)}`);
                  return;
              }
              showError("메시지방을 시작하지 못했습니다.");
          } catch {
              showError("메시지방을 시작하지 못했습니다.");
          }
      };
    return (
        <div className="tour-detail-page tour-detail-page--view" style={{ paddingBottom: ctaHeight }}>
            {!fromAdminData && (
                <Seo
                    title={tour.name}
                    description={tour.name ? `${tour.name} 다이빙 투어 일정과 가격을 확인하고 예약하세요.` : undefined}
                    image={carouselImages?.[0]?.image || carouselImages?.[0]}
                />
            )}
            <SubHeader
                title={fromAdminData ? "미리보기" : tour.name}
                className={fromAdminData ? "tour-preview-header" : ""}
                menus={fromAdminData ? [] : [{
                    name: '찜',
                    ariaLabel: isLiked(tourId) ? '찜 해제' : '찜하기',
                    content: <TourHeartIcon liked={isLiked(tourId)} />,
                    callback: async () => {
                        const handled = await toggleLike(tourId);
                        if (!handled) setShowLoginBottomSheet(true);
                    },
                }, {
                    name: '공유',
                    icon: shareIcon,
                    callback: () => share({ title: tour.name, appPath: `/tour/detail/${tourId}` }),
                }]}
            />
            {/* 썸네일 캐러셀 */}
            <div style={{ marginTop: 48 }}>
                <ThumbnailCarousel
                    images={carouselImages ?? []}
                    height={390}
                />
            </div>

            {/* ① 핵심 정보 (탭 위 고정 영역) */}
            <div id="td-info" className="td-section td-section--info">
                <div
                    style={{
                        fontSize: 20,
                        fontWeight: 800,
                        color: "#333",
                        lineHeight: 1.3,
                        marginBottom: 6,
                    }}
                >
                    {tour.name}
                </div>
                <div style={{ fontSize: 13, color: "#999", marginBottom: 16 }}>
                    📍 {tour.location}
                </div>
                <div className="td-price-group">
                    <div>
                        <div className="td-price-group-label">예약금</div>
                        <div className="td-price-input-wrap">
                            <span className="td-price">{fmt(tour.price)}</span>
                            <span className="td-price-unit">원</span>
                        </div>
                    </div>
                    <div>
                        <div className="td-price-group-label">강사 프로그램 비용{tour.balance_currency ? `(${getCurrencyConfig(tour.balance_currency).unit})` : ''}</div>
                        <div className="td-price-input-wrap">
                            {tour.currency && getCurrencyConfig(tour.balance_currency).prefix && (
                                <span className="td-price-unit td-price-unit--usd">{getCurrencyConfig(tour.balance_currency).symbol}</span>
                            )}
                            <span className="td-price">{fmt(tour.balance_usd)}</span>
                            {tour.currency && !getCurrencyConfig(tour.balance_currency).prefix && (
                                <span className="td-price-unit">{getCurrencyConfig(tour.balance_currency).unit}</span>
                            )}
                        </div>
                    </div>
                </div>
                {activeOptions.length > 0 && (
                    <div className="td-option-checks" style={{ pointerEvents: "none" }}>
                        {activeOptions.map((label) => (
                            <label key={label} className="td-option-check">
                                <input type="checkbox" checked readOnly />
                                <span>{label}</span>
                            </label>
                        ))}
                    </div>
                )}
                {/* ② 참가자 현황 */}
                {isLimited && tour.participants != null && (
                    <div>
                        <div className="td-participants-summary ">
                          <div className="td-participants-toggle">
                          <span className="td-price-group-label">{`현재 신청자 (`}</span>
                                
                                <div className="td-participants-meta td-price-group-label">
                                    <span className="td-participants-current">{tour.confirmed_count}명</span>
                                    <span className="td-participants-sep"> / </span>
                                    <span className="td-participants-max">{tour.max_participants}명 )</span>
                                </div>
                                
                          </div>
                            <button
                                className={`td-participants-toggle${participantsExpanded ? " td-participants-toggle--open" : ""}`}
                                onClick={() => setParticipantsExpanded((v) => !v)}
                                aria-label="참가자 목록 펼치기"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                        {/* <div className="td-participants-bar-wrap">
                            <div
                                className="td-participants-bar-fill"
                                style={{ width: `${Math.min((tour.confirmed_count / tour.max_participants) * 100, 100)}%` }}
                            />
                        </div> */}
                        {participantsExpanded && tour.participants.length > 0 && (
                            <div className="td-participants-carousel-wrap">
                                <div className="td-participants-carousel">
                                    {tour.participants.map((p, idx) => {
                                        const u = p.user || {};
                                        return (
                                            <div key={idx} className="td-participants-carousel__cell">
                                                <PersonChip
                                                    name={maskName(u.name || p.name, !!userInfo)}
                                                    imageUrl={u.profile_image_url}
                                                    userId={getPublicUserKey(u)}
                                                    size={44}
                                                />
                                                {p.companion_count > 0 && (
                                                    <div className="td-participants-carousel__companion">
                                                        +동반 {p.companion_count}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {/* ② ALWAYS 투어 회차별 참가 현황 */}
                {!isLimited && departures.length > 0 && (
                    <div>
                        <div className="td-participants-summary">
                            <button
                                className={`td-participants-toggle${participantsExpanded ? " td-participants-toggle--open" : ""}`}
                                onClick={() => setParticipantsExpanded((v) => !v)}
                                aria-label="회차별 참가 현황 펼치기"
                            >
                                <div className="td-participants-meta">
                                    <span>일정별 참가 현황</span>
                                    <span className="td-departure-badge">{departures.length}개 일정</span>
                                </div>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                        {participantsExpanded && (
                            <div className="td-departure-list">
                                {departures.map((dep) => (
                                    <div key={dep.id} className="td-departure-row">
                                        <div className="td-departure-date">
                                            {dep.start_date}
                                            {dep.end_date && dep.end_date !== dep.start_date ? ` ~ ${dep.end_date}` : ""}
                                        </div>
                                        <div className="td-departure-count">
                                            <span className="td-departure-current">{dep.confirmed_count || 0}명</span>
                                            <span className="td-departure-sep"> / </span>
                                            <span className="td-departure-max">{dep.capacity}명</span>
                                        </div>
                                        <div className="td-participants-bar-wrap">
                                            <div
                                                className="td-participants-bar-fill"
                                                style={{ width: `${Math.min(((dep.confirmed_count || 0) / (dep.capacity || 1)) * 100, 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 탭 네비게이션 */}
            <TabHeader
                menus={menus}
                defaultTab={selectedTab}
                setSelectedTab={setSelectedTab}
            />

            {/* 탭 콘텐츠 */}
            <div className="tab-content-wrap">
                {selectedTab === "상세정보" && (
                    <>

                        {!isLimited && (
                            <div className="td-always-tour-noti">
                                [상시모집] "더 딥스 보홀 센터"에서 매일매일 진행되는 더딥스 전용 투어입니다.
                            </div>
                        )}
                        {/* ③ 인솔강사 */}
                        {guide && (
                            <div id="td-instructor" className="td-section">
                                <div className="td-section-title">인솔 강사</div>
                                <div className="td-instructor-cards">
                                    {guides.map((g, i) => {
                                        const profileKey = getPublicUserKey(g);
                                        const contact = [g.hp, g.email || g.user_email].filter(Boolean).join(" · ");
                                        return (
                                            <div key={i} className="instructor-card">
                                                <div
                                                    className="instructor-card__body"
                                                    onClick={() => {
                                                        if (profileKey) {
                                                            navigate(`/instructor/${encodeURIComponent(profileKey)}`);
                                                        }
                                                    }}
                                                    style={{ cursor: profileKey ? "pointer" : "default" }}
                                                >
                                                    <ProfileAvatar
                                                        imageUrl={g.profile_image_url}
                                                        size={56}
                                                    />
                                                    <div className="instructor-card__info">
                                                        <div className="instructor-card__row instructor-card__row--top">
                                                            <span className="instructor-card__name">{g.name}</span>
                                                            {g.brand && (
                                                                <span className="instructor-card__brand">{g.brand}</span>
                                                            )}
                                                            <span className="instructor-card__role">강사</span>
                                                        </div>
                                                        {contact && (
                                                            <div className="instructor-card__row instructor-card__row--bottom">
                                                                {contact}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                              <button
                                                  type="button"
                                                  className="community-profile-wrap__dm-btn"
                                                  onClick={() => {handleStartMessage(g.public_id)}}
                                                  aria-label="메세지 보내기"
                                              >
                                                  메세지
                                              </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            
                            </div>
                        )}

                        {tour.intro_desc && (
                          <div className="td-section">
                            <div className="td-section-title">투어 소개</div>
                            <div className="td-instructor-quote" style={{ whiteSpace: "pre-line" }}>{renderWithLinks(tour.intro_desc)}</div>
                          </div>
                        )}

                        {/* ③ 투어 기간 */}
                        {isLimited && (
                            <div className="td-section td-section--dates">
                                <div className="td-section-title">투어 기간</div>
                                <div className="td-date-row">
                                    <div className="td-date-field">
                                        <span className="td-date-label">출발 날짜</span>
                                        <div className="td-datepicker-input" style={{ cursor: "default" }}>
                                            {tour.start_date}
                                        </div>
                                    </div>
                                    <span className="td-date-sep">~</span>
                                    <div className="td-date-field">
                                        <span className="td-date-label">귀국 날짜</span>
                                        <div className="td-datepicker-input" style={{ cursor: "default" }}>
                                            {tour.end_date}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ④ 투어 일정 */}
                        {isLimited && (
                            <div id="td-schedule" className="td-section">
                                <div className="td-section-title">투어 일정</div>
                                {tour.schedule?.length > 0 ? (
                                    tour.schedule.map((s, sIdx) => (
                                        <div key={sIdx} className="td-schedule-item">
                                            <div className="td-schedule-left">
                                                <div
                                                    className={`td-day-badge${
                                                        sIdx === tour.schedule.length - 1
                                                            ? " td-day-badge--gray"
                                                            : ""
                                                    }`}
                                                >
                                                    D{s.day}
                                                </div>
                                                {sIdx < tour.schedule.length - 1 && (
                                                    <div className="td-day-line" />
                                                )}
                                            </div>
                                            <div className="td-schedule-body">
                                                <div className="td-schedule-date">
                                                    {fmtScheduleDate(s.date)}
                                                </div>
                                                <div className="td-schedule-items">
                                                    {s.items.map((item, iIdx) => (
                                                        <div key={iIdx}>{renderWithLinks(item)}</div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : null}
                            </div>
                        )}

                        {/* ⑤ 모집 인원 & 참가 자격 */}
                        <div className="td-section">
                            <div className="td-section-title">모집 인원 & 참가 자격</div>
                            <div className="td-capacity-row">
                                <div className="td-capacity-card td-capacity-card--purple">
                                    <div className="td-capacity-label">최소 출발</div>
                                    <div className="td-capacity-num">{tour.min_participants}명</div>
                                </div>
                                <div className="td-capacity-card td-capacity-card--purple">
                                    <div className="td-capacity-label">최대 인원</div>
                                    <div className="td-capacity-num">{tour.max_participants}명</div>
                                </div>
                            </div>
                            <div className="td-qualify-box">
                                <div className="td-qualify-title">참가 자격 요건</div>
                                {tour.qualifications && (
                                    <div className="td-qualify-text" style={{ whiteSpace: "pre-line" }}>
                                        {renderWithLinks(tour.qualifications)}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 투어 상세 페이지 (에디터 본문 HTML을 TuiViewer로 렌더링) */}
                        {hasDetailContent && (
                          <div className="td-section">
                              <div className="td-section-title">투어 추가정보</div>
                              <TuiViewer htmlString={tour.detailed_content} className="td-detailed-content" />
                          </div>

                        )}
                        {/* ⑥ 투어 비용 */}
                        <div id="td-cost" className="td-section">
                            <div className="td-section-title">투어 비용</div>
                            <div className="td-cost-table">
                                <div className="td-cost-row td-cost-row--header">
                                    <span className="td-cost-label td-cost-label--brand">투어 예약금</span>
                                    <span className="td-cost-value td-cost-value--brand">
                                        {fmt(tour.price)}원
                                    </span>
                                </div>
                                {(tour.include_items || []).length > 0 ? (
                                    (tour.include_items || []).map((c, i) => (
                                        <div key={i} className="td-cost-row">
                                            <span className="td-cost-label">{c.label}</span>
                                            <div
                                                className={`td-cost-switch${c.include ? "" : " td-cost-switch--extra"}`}
                                                style={{ pointerEvents: "none" }}
                                            >
                                                <span className="td-cost-switch-opt td-cost-switch-opt--in">포함</span>
                                                <span className="td-cost-switch-opt td-cost-switch-opt--ex">별도</span>
                                            </div>
                                        </div>
                                    ))
                                ) : null}
                            </div>
                            <div className="td-section-warning">
                                위 표에 '별도'로 표기된 항목 외에는 추가로 요청되는 비용이 없습니다.<br/>
                                표기되지 않은 항목의 비용을 사전 또는 현장에서 안내받으신 경우
                                버킷리스트클래스 상담채널로 문의해 주세요.
                            </div>
                            {Number(tour.single_room_surcharge) > 0 && (
                                <div className="td-single-room">
                                    <span className="td-single-room-label">1인실 변경 시 추가 비용</span>
                                    <span className="td-single-room-price">
                                        +{fmt(tour.single_room_surcharge)}원
                                    </span>
                                </div>
                            )}
                        </div>


                        {/* ⑦ 환불 규정 */}
                        <div className="td-section td-section--last">
                            <div className="td-section-title">환불 규정</div>
                            <div className="td-refund-table">
                                {(tour.refund_policy || []).length > 0 ? (
                                    (tour.refund_policy || []).map((r, i) => (
                                        <div
                                            key={i}
                                            className={`td-refund-row td-refund-row--${getRefundColorClass(r.color)}`}
                                        >
                                            <span className="td-refund-period">{r.period}</span>
                                            <span className={`td-refund-rate td-refund-rate--${getRefundColorClass(r.color)}`}>
                                                {r.rate}
                                            </span>
                                        </div>
                                    ))
                                ) : null}
                            </div>
                            {tour.refund_notice && (
                                <div className="td-refund-notice" style={{ whiteSpace: "pre-line" }}>
                                    {tour.refund_notice}
                                </div>
                            )}
                        </div>
                    </>
                )}

                {selectedTab === "문의" && (
                    <NoData
                        title="문의는 준비중입니다."
                        desc="빠른 시일 내에 준비하여 오픈하겠습니다."
                    />
                )}
            </div>

            {/* 하단 고정 CTA */}
            <div className="td-cta" ref={ctaRef}>
                {/* ALWAYS 투어: 일정 드롭다운 */}
                {!isLimited && (
                    <div className="td-cta-schedule">
                        <select
                            className="td-cta-select"
                            value={scheduleValue}
                            onChange={(e) => {
                                const val = e.target.value;
                                setScheduleValue(val);
                                if (val !== "custom") {
                                    setDesiredStartDate("");
                                    setDesiredEndDate("");
                                }
                            }}
                        >
                            <option value="">투어 일정을 선택하세요</option>
                            {departures.map((dep) => (
                                <option key={dep.id} value={String(dep.id)}>
                                    {dep.start_date}
                                    {dep.end_date && dep.end_date !== dep.start_date ? ` ~ ${dep.end_date}` : ""}
                                    {" "}({dep.confirmed_count || 0}명 참여중)
                                </option>
                            ))}
                            <option value="custom">직접 입력</option>
                        </select>
                        {scheduleValue === "custom" && (
                            <div className="td-cta-date-row">
                                <input
                                    type="date"
                                    value={desiredStartDate}
                                    onChange={(e) => setDesiredStartDate(e.target.value)}
                                    min={new Date().toISOString().split("T")[0]}
                                    placeholder="시작일"
                                />
                                <span className="td-cta-date-sep">~</span>
                                <input
                                    type="date"
                                    value={desiredEndDate}
                                    onChange={(e) => setDesiredEndDate(e.target.value)}
                                    min={desiredStartDate || new Date().toISOString().split("T")[0]}
                                    placeholder="종료일"
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* 가격 + 수량 + 예약하기 */}
                <div className="td-cta-bottom">
                    <div className="td-cta-price-wrap">
                        <div className="td-cta-price-label">예약금</div>
                        <div className="td-cta-price">{fmt(tour.price * purchaseCnt)}원</div>
                    </div>
                    <div className="td-cta-qty">
                        <button
                            className="td-cta-qty-btn"
                            onClick={() => {
                                const next = Math.max(1, purchaseCnt - 1);
                                setPurchaseCnt(next);
                            }}
                            disabled={purchaseCnt <= 1}
                        >
                            −
                        </button>
                        <span className="td-cta-qty-num">{purchaseCnt}</span>
                        <button
                            className="td-cta-qty-btn"
                            onClick={() => setPurchaseCnt(Math.min(tour.max_participants || 99, purchaseCnt + 1))}
                            disabled={purchaseCnt >= (tour.max_participants || 99)}
                        >
                            +
                        </button>
                    </div>
                    <button
                        className={`td-cta-btn  ${tour.confirmed_count === tour.max_participants ? 'btn btn-disabled':''}`}
                        onClick={() => {
                            if (!userInfo) {
                                setShowLoginBottomSheet(true);
                                return;
                            }
                            if (!isLimited) {
                                if (!scheduleValue) {
                                    showError("투어 일정을 선택해주세요.");
                                    return;
                                }
                                if (scheduleValue === "custom" && !desiredStartDate) {
                                    showError("희망 출발일을 입력해주세요.");
                                    return;
                                }
                            }
                            const qp = new URLSearchParams({ purchaseCnt });
                            if (!isLimited) {
                                if (scheduleValue && scheduleValue !== "custom") {
                                    qp.set("departureId", scheduleValue);
                                } else if (scheduleValue === "custom" && desiredStartDate) {
                                    qp.set("startDate", desiredStartDate);
                                    if (desiredEndDate) qp.set("endDate", desiredEndDate);
                                }
                            }
                            navigate(`/payment/tour/${tourId}?${qp.toString()}`);
                        }}
                        disabled={tour.confirmed_count === tour.max_participants}
                >
                        {tour.confirmed_count === tour.max_participants ? '모집마감':'예약하기' }
                    </button>
                </div>
            </div>

            <LoginBottomSheet
                isOpen={showLoginBottomSheet}
                onClose={() => setShowLoginBottomSheet(false)}
            />
        </div>
    );
};

export default TourDetail;
